import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, X, MessageCircle, Check, CheckCheck } from 'lucide-react';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) 
  ? 'http://localhost:8001/api' 
  : '/api';

const LiveChatSupport = ({ user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [welcomeSent, setWelcomeSent] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    initializeChat();
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const initializeChat = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }
      
      // Check for existing chat first
      let chatData = null;
      
      try {
        const existingResponse = await axios.get(`${API}/live-chat/my-chat`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (existingResponse?.data) {
          chatData = existingResponse.data;
        }
      } catch (err) {
        // No existing chat, will create new one
        console.log('No existing chat found, creating new one');
      }
      
      if (!chatData) {
        // Start new chat
        const newChatResponse = await axios.post(`${API}/live-chat/start`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        chatData = newChatResponse.data;
      }
      
      setChat(chatData);
      setLoading(false);
      
      // Fetch initial messages
      await fetchMessages(chatData.id);

      // Start polling for new messages every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        if (chatData?.id) {
          fetchMessages(chatData.id);
        }
      }, 3000);

    } catch (err) {
      console.error('Error initializing chat:', err);
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId) => {
    if (!chatId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/live-chat/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fetchedMessages = response.data || [];
      setMessages(fetchedMessages);
      
      // Check if welcome message should be shown
      // Only show welcome if there are no messages or no admin messages yet
      if (!welcomeSent && fetchedMessages.length === 0) {
        // No messages yet - don't show welcome until user sends first message
      }
      
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !chat || sending) return;

    const messageToSend = newMessage.trim();
    setSending(true);
    setNewMessage('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setSending(false);
        setNewMessage(messageToSend);
        return;
      }
      
      console.log('Sending message to chat:', chat.id);
      
      const response = await axios.post(
        `${API}/live-chat/${chat.id}/messages`,
        { message: messageToSend },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log('Message sent successfully:', response.data);
      
      // Send automatic welcome message if this is the first user message
      const currentMessages = messages.filter(m => m.sender_role === 'USER');
      if (currentMessages.length === 0 && !welcomeSent) {
        setWelcomeSent(true);
        
        // Add welcome message locally (simulating admin auto-reply)
        setTimeout(async () => {
          try {
            // This would be better handled by backend, but for now we'll add locally
            const welcomeMsg = {
              id: `welcome-${Date.now()}`,
              chat_id: chat.id,
              sender_id: 'system',
              sender_name: 'Destek Ekibi',
              sender_role: 'ADMIN',
              message: 'Merhaba! Destek ekibimize hoş geldiniz. Size en kısa sürede yardımcı olacağız. Lütfen sorununuzu detaylı bir şekilde açıklayın.',
              created_at: new Date().toISOString(),
              read: false
            };
            setMessages(prev => [...prev, welcomeMsg]);
          } catch (err) {
            console.error('Error adding welcome message:', err);
          }
        }, 1000);
      }
      
      // Fetch updated messages
      await fetchMessages(chat.id);
      
    } catch (err) {
      console.error('Error sending message:', err);
      console.error('Error details:', err.response?.data);
      setNewMessage(messageToSend);
      alert('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // Auto scroll to bottom when new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Canlı destek bağlantısı kuruluyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-900" style={{ height: 'calc(100vh - 64px)', maxHeight: 'calc(100vh - 64px)' }}>
      {/* Header - Fixed */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <MessageCircle className="text-white" size={20} />
          </div>
          <div>
            <div className="font-bold text-white">Canlı Destek</div>
            <div className="text-xs text-white/80 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Online
            </div>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Messages Area - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="mx-auto mb-3 text-gray-600" size={48} />
              <p className="text-gray-500">Destek ekibimize mesaj gönderin</p>
              <p className="text-gray-600 text-sm mt-1">Size en kısa sürede yardımcı olacağız</p>
            </div>
          </div>
        ) : (
          messages.map((msg) => {
            const isUser = msg.sender_role === 'USER';
            return (
              <div
                key={msg.id}
                className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isUser
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {!isUser && (
                    <div className="text-xs font-semibold mb-1 text-blue-400">
                      {msg.sender_name || 'Destek'}
                    </div>
                  )}
                  <p className="break-words">{msg.message}</p>
                  <div className={`flex items-center gap-1 justify-end mt-1 text-xs ${
                    isUser ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    <span>{formatTime(msg.created_at)}</span>
                    {isUser && (
                      msg.read ? (
                        <CheckCheck size={14} className="text-blue-200" />
                      ) : (
                        <Check size={14} className="text-blue-300" />
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="bg-gray-800 border-t border-gray-700 p-3 flex-shrink-0">
        <form onSubmit={sendMessage} className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Mesajınızı yazın..."
            className="flex-1 bg-gray-700 text-white px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white p-3 rounded-full transition-colors flex-shrink-0"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LiveChatSupport;
